import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { toast } from "sonner";
import { BUILD_ID, BUILD_DATE } from "../build-info";

/**
 * AboutCard — shows the installed app version + build number.
 *
 * Why this exists: when the user reports a bug on TestFlight / production
 * we need a fast, unambiguous way to know which .ipa they're actually
 * running. Build numbers in App Store Connect can lag behind what's on
 * the device (TestFlight caching), and users can't easily read the build
 * number from anywhere in iOS. Exposing it in Settings gives support
 * (and the reviewer) a one-tap answer.
 *
 * Native (iOS/Android): pulls the real values from CFBundleShortVersionString
 * / CFBundleVersion via @capacitor/app.
 * Web/PWA: shows "Web" — build info is not meaningful in that context.
 *
 * Long-press the version pill → copies it to the clipboard, so it's
 * trivial to paste into a support message.
 */
export default function AboutCard() {
  const [info, setInfo] = useState({
    platform: Capacitor.getPlatform(),
    version: null,
    build: null,
    ready: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!Capacitor.isNativePlatform()) {
        if (!cancelled)
          setInfo((prev) => ({ ...prev, ready: true, version: "Web", build: "—" }));
        return;
      }
      try {
        const data = await App.getInfo();
        if (cancelled) return;
        setInfo({
          platform: Capacitor.getPlatform(),
          version: data.version,
          build: data.build,
          ready: true,
        });
      } catch {
        if (!cancelled)
          setInfo((prev) => ({ ...prev, ready: true, version: "unknown", build: "unknown" }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const summary = info.ready
    ? `Binshare ${info.version} (${info.build}) · ${info.platform}`
    : "Loading…";

  // Compact commit reference for the "copy" payload so support can grep
  // exactly which JS bundle the user is running. Deliberately not shown
  // in the visible UI text (keeps the card clean) — but included in what
  // the Copy button writes to the clipboard.
  const detail = BUILD_ID && BUILD_ID !== "dev"
    ? `${summary} · build ${BUILD_ID}${BUILD_DATE ? ` (${BUILD_DATE})` : ""}`
    : summary;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(detail);
      toast.success("Version copied");
    } catch {
      toast.error("Couldn't copy — please screenshot instead");
    }
  };

  return (
    <Card
      data-testid="about-card"
      className="mt-6 rounded-2xl border border-[#E2DFD8] bg-white p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-[#6B6965]">About</div>
          <div
            data-testid="about-version"
            className="mt-1 text-sm font-medium text-[#1B3624] truncate"
            style={{ fontFamily: "Outfit" }}
          >
            {summary}
          </div>
          <div className="text-[11px] text-[#6B6965] mt-1">
            Share this with support if something isn&apos;t working.
          </div>
        </div>
        <button
          type="button"
          data-testid="about-copy"
          onClick={copy}
          className="rounded-full border border-[#E2DFD8] bg-white px-3 py-1.5 text-xs text-[#1B3624] hover:border-[#1B3624] active:bg-[#F6F4F0] min-h-[36px] shrink-0"
        >
          Copy
        </button>
      </div>
    </Card>
  );
}
